/**
 * Fetch and normalize adjustment reason catalog rows for the admin table.
 *
 * The backend list endpoint does not support a real "all" filter param and uses
 * icontains for role_scope. This module fans out parallel GET requests when the
 * UI filter is "Todos", then applies client-side normalization.
 */

import {
  ADJUSTMENT_REASON_LABEL,
  FILTER_DIMENSIONS,
  activeScopeValues,
  filterDimensionOptions,
} from './constants';

/**
 * Builds one or more query-param objects for parallel API calls.
 *
 * - "Todos" on a dimension => expand to all values in filterDimensionOptions.
 * - All dimensions "Todos" => single request with no params.
 * - Otherwise => cartesian product of selected + expanded values.
 */
export const buildFilterRequests = (filters) => {
  const isAllSelected = (key) => !filters[key];

  if (FILTER_DIMENSIONS.every(isAllSelected)) {
    return [{}];
  }

  return FILTER_DIMENSIONS.reduce((combinations, key) => {
    const selectedValue = filters[key];
    const values = selectedValue
      ? [selectedValue]
      : filterDimensionOptions[key].map((option) => option.value);

    return combinations.flatMap((combination) =>
      values.map((value) => ({ ...combination, [key]: value })),
    );
  }, [{}]);
};

/** Same reason id can appear in multiple fan-out responses. */
export const dedupeReasons = (reasons) => {
  const byId = new Map();
  reasons.forEach((reason) => {
    if (reason?.id) {
      byId.set(reason.id, reason);
    }
  });
  return Array.from(byId.values());
};

const applyExactRoleScopeFilter = (reasons, roleScopeFilter) => {
  if (!roleScopeFilter) return reasons;
  return reasons.filter((reason) => reason.role_scope === roleScopeFilter);
};

const filterActiveScopeReasons = (reasons) =>
  reasons.filter((reason) => activeScopeValues.has(reason.scope));

const filterManagedLabelReasons = (reasons) =>
  reasons.filter((reason) => reason.label === ADJUSTMENT_REASON_LABEL);

/**
 * Rows shown in the table: exact role, active scopes, label "other" only.
 */
export const normalizeCatalogForTable = (reasons, roleScopeFilter) =>
  filterManagedLabelReasons(
    filterActiveScopeReasons(applyExactRoleScopeFilter(reasons, roleScopeFilter)),
  );

/**
 * Loads catalog rows for the current UI filters.
 */
export const fetchFilteredAdjustmentReasons = async (authToken, filters, getAdjustmentReasons) => {
  const filterRequests = buildFilterRequests(filters);
  const responses = await Promise.all(
    filterRequests.map((params) => getAdjustmentReasons(authToken, params)),
  );

  return normalizeCatalogForTable(dedupeReasons(responses.flat()), filters.role_scope);
};
